import { type FC, useState } from "react";
import { Button } from "antd";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import WarningConfirmationPopover from "../../../../shared/components/WarningConfirmationPopover";
import { ReactComponent as AddIcon } from "../../../../assets/images/add-18.svg";
import { restrictToTwoDecimals } from "../../../create-protocol/utils";
import StdSeedRate from "../StdSeedRate";
import StdRateFields from "../StdSeedRate/StdRateFields";
import PlotSummary from "../PlotSummary";
import AutoProdAssignment from "../AutoProdAssignment";
import { ErrorAutoScreen } from "../ErrorAutoScreen";
import NutrientTable from "../NutrientTable";
import { type NutrientState } from "../../hooks/useNutrientState";
import { groupLetter } from "../../utils/nutrientPlots";
import { type ApplyRateError } from "../../utils/nutrientValidation";
import "./style.less";

export interface NutrientProps {
  state: NutrientState;
}

const Nutrient: FC<NutrientProps> = ({ state }) => {
  const { t } = useTranslation();
  const [overwriteOpen, setOverwriteOpen] = useState(false);
  const isStripes = state.layout === "stripes";
  const colorScaleRates = state.groups.flatMap((group) =>
    group.plots.map((plot) => state.applyByPlot[plot.id])
  );
  const handleAutoAssign = (): void => {
    if (!state.canAutoAssign) return;
    if (state.assigned > 0) {
      setOverwriteOpen(true);
      return;
    }
    void state.autoAssign();
  };
  const applyErrorMessage = (error: ApplyRateError): string =>
    formatTranslation(
      error.kind === "range"
        ? t("newRatesAndDosage.nutrient.applyRangeError", {
            min: error.min,
            max: error.max,
          })
        : t("newRatesAndDosage.nutrient.applySpacingError", {
            previous: error.previous,
            current: error.current,
            distance: error.distance,
          })
    );

  if (state.autoAssignmentFailed) {
    return (
      <ErrorAutoScreen
        isRetrying={state.isAutoAssigning}
        onRetry={() => {
          void state.autoAssign();
        }}
        onClose={state.closeAutoAssignmentError}
      />
    );
  }

  return (
    <div className="rd-nutrient" data-testid="nutrient">
      <StdSeedRate
        className="rd-std-seed-rate--nutrient"
        title={formatTranslation(t("newRatesAndDosage.nutrient.stdRateTitle"))}
        tooltip={formatTranslation(
          t("newRatesAndDosage.nutrient.stdRateTooltip")
        )}
        fields={state.fertilisers.map((fertiliser) => (
          <StdRateFields
            key={fertiliser.id}
            productLabel={formatTranslation(
              t(
                fertiliser.nutrientValue
                  ? "newRatesAndDosage.nutrient.nutrientLabel"
                  : "newRatesAndDosage.nutrient.fertiliserLabel"
              )
            )}
            productPlaceholder={formatTranslation(
              t("newRatesAndDosage.stdSeedRate.productPlaceholder")
            )}
            productOptions={state.nutrientOptionsFor(fertiliser.id)}
            productValue={fertiliser.nutrientValue}
            onProductChange={(value) => {
              state.setFertiliserNutrient(fertiliser.id, value);
            }}
            productError={
              fertiliser.touched && !fertiliser.nutrientValue?.trim()
                ? formatTranslation(
                    t("newRatesAndDosage.nutrient.fertiliserError")
                  )
                : undefined
            }
            rateLabel={formatTranslation(
              t("newRatesAndDosage.nutrient.rateLabel")
            )}
            rateValue={fertiliser.rate}
            ratePrecision={2}
            rateStep={0.01}
            onRateKeyDown={restrictToTwoDecimals}
            onRateChange={(value) => {
              state.setFertiliserRate(fertiliser.id, value);
            }}
            rateError={
              fertiliser.touched &&
              Boolean(fertiliser.nutrientValue?.trim()) &&
              !(fertiliser.rate !== null && fertiliser.rate > 0)
                ? formatTranslation(t("newRatesAndDosage.nutrient.rateError"))
                : undefined
            }
            deleteLabel={formatTranslation(
              t("newRatesAndDosage.nutrient.deleteFertiliser")
            )}
            onDelete={
              state.fertilisers.length > 1
                ? () => {
                    state.removeFertiliser(fertiliser.id);
                  }
                : undefined
            }
          />
        ))}
      >
        <div className="rd-nutrient__std-rate-footer">
          <Button
            className="rd-nutrient__more-btn"
            type="text"
            icon={<AddIcon aria-hidden />}
            onClick={state.addFertiliser}
          >
            {formatTranslation(t("newRatesAndDosage.nutrient.moreNutrients"))}
          </Button>
          {state.totalRate > 0 && (
            <span
              className="rd-nutrient__total-product"
              data-testid="nutrient-total-product"
            >
              {formatTranslation(
                t("newRatesAndDosage.nutrient.totalProduct", {
                  value: state.totalRate,
                })
              )}
            </span>
          )}
        </div>
      </StdSeedRate>

      <PlotSummary
        totalPlots={state.totalPlots}
        assigned={state.assigned}
        levels={state.levels}
        totalLabel={
          isStripes
            ? formatTranslation(t("newRatesAndDosage.plotSummary.totalStripes"))
            : undefined
        }
      />

      {state.isFullyAssigned &&
        state.requiredLevels !== undefined &&
        state.levels < state.requiredLevels && (
          <p className="rd-nutrient__validation-error" role="alert">
            {formatTranslation(
              t("newRatesAndDosage.nutrient.requiredLevelsError", {
                required: state.requiredLevels,
                actual: state.levels,
              })
            )}
          </p>
        )}

      <WarningConfirmationPopover
        open={overwriteOpen}
        message={formatTranslation(
          t("newRatesAndDosage.seeds.overwriteConfirmation")
        )}
        confirmText={formatTranslation(t("newRatesAndDosage.seeds.proceed"))}
        cancelText={formatTranslation(t("newRatesAndDosage.seeds.cancel"))}
        confirmDisabled={!state.canAutoAssign}
        onCancel={() => {
          setOverwriteOpen(false);
        }}
        onConfirm={() => {
          setOverwriteOpen(false);
          void state.autoAssign();
        }}
      >
        <div className="rd-nutrient__auto-assignment">
          <AutoProdAssignment
            disabled={!state.canAutoAssign}
            loading={state.isAutoAssigning}
            onClick={handleAutoAssign}
          />
        </div>
      </WarningConfirmationPopover>

      {state.groups.map((group) => (
        <NutrientTable
          key={group.id}
          className={isStripes ? "rd-nutrient-table--stripes" : undefined}
          title={
            group.groupIndex === undefined
              ? formatTranslation(t("newRatesAndDosage.nutrient.stripes"))
              : formatTranslation(
                  t("newRatesAndDosage.nutrient.groupLabel", {
                    letter: groupLetter(group.groupIndex),
                  })
                )
          }
          groupColumnLabel={
            isStripes
              ? formatTranslation(t("newRatesAndDosage.nutrient.stripes"))
              : undefined
          }
          rowLabel={formatTranslation(
            t(
              isStripes
                ? "newRatesAndDosage.nutrient.stripeColumn"
                : "newRatesAndDosage.nutrient.plotColumn"
            )
          )}
          nutrientLabel={
            !state.nutrientCode || state.nutrientCode === "N"
              ? formatTranslation(t("newRatesAndDosage.nutrient.nitrogen"))
              : state.nutrientCode
          }
          rows={state.rowsByGroup[group.id]}
          colorScaleRates={colorScaleRates}
          expanded={state.expandedGroups[group.id] ?? false}
          onToggle={() => {
            state.toggleGroup(group.id);
          }}
          onApplyChange={state.setApply}
          onTogglePlot={state.togglePlotHighlight}
          highlightedIds={state.highlightedPlotIds}
          errors={Object.fromEntries(
            group.plots.map((plot) => [
              plot.id,
              (state.applyErrorsByPlot[plot.id] ?? []).map(applyErrorMessage),
            ])
          )}
          highlightMissing={state.assigned > 0}
        />
      ))}
    </div>
  );
};

export default Nutrient;
